import {
	AuditActionTypeCell,
	AuditTenantsCell
} from '@components/Audits/DataGridCells';
import FlowStatusCell from '@components/Flows/DataGridCells/FlowStatusCell';
import {
	AccessKeyStatusCell,
	ExpirationCell,
	UserEmailCell,
	UserPhoneCell,
	UserStatusCell
} from '@components/Users/DataGridCells';
import ProjectsCell from '@components/Users/DataGridCells/ProjectsCell';
import { GridCellParams, GridColDef } from '@mui/x-data-grid/models';
import {
	ACCESS_KEYS_FIELDS,
	AUDITS_FIELDS,
	COMMON_FIELDS,
	FLOWS_FIELDS,
	PERMISSIONS_FIELDS,
	ROLES_FIELDS,
	TENANTS_FIELDS,
	USERS_FIELDS
} from 'constants/global';
import { compact } from 'lodash';
import { IPermission } from 'types/authorization';
import { UserStatus } from 'types/common';
import { getActionCell, getActionsColumn } from './datagrid/actions';
import { getAuthColumns, getColumn, getDateColumn } from './datagrid/columns';
import { ColWidth } from './datagrid/common';
import { getExternalIdsString } from './users';

type TableCellAction = (item: any) => void;

const COMMON_PREFIX = 'fields.common.column';
export const getCommonColumns = (): GridColDef[] => [
	getColumn(COMMON_PREFIX, COMMON_FIELDS.id, ColWidth.s),
	getColumn(COMMON_PREFIX, COMMON_FIELDS.version, ColWidth.m),
	getDateColumn(COMMON_PREFIX, COMMON_FIELDS.modifiedTime),
	getDateColumn(COMMON_PREFIX, COMMON_FIELDS.createdTime)
];

export const getUsersColumns = (
	editSingleAction?: TableCellAction,
	deleteAction?: TableCellAction,
	suspendAction?: TableCellAction,
	activateAction?: TableCellAction
): GridColDef[] => {
	const prefix = 'fields.users.column';

	return [
		getColumn(
			prefix,
			USERS_FIELDS.externalIDs,
			ColWidth.l,
			undefined,
			(params: GridCellParams) => getExternalIdsString(params.value)
		),
		getColumn(prefix, USERS_FIELDS.status, ColWidth.m, UserStatusCell),
		getColumn(prefix, USERS_FIELDS.displayName, ColWidth.m),
		getColumn(prefix, USERS_FIELDS.email, ColWidth.xl, UserEmailCell),
		getColumn(prefix, USERS_FIELDS.phoneNumber, ColWidth.m, UserPhoneCell),
		getDateColumn(prefix, USERS_FIELDS.logoutTime),
		getDateColumn(prefix, USERS_FIELDS.createdTime),
		...getAuthColumns(),
		getColumn(COMMON_PREFIX, USERS_FIELDS.version, ColWidth.m),
		getColumn(COMMON_PREFIX, USERS_FIELDS.id, ColWidth.m),
		getDateColumn(COMMON_PREFIX, USERS_FIELDS.modifiedTime),
		getActionsColumn(({ row }) => {
			const res = [];
			if (editSingleAction) {
				res.push(
					getActionCell(
						() => editSingleAction!(row),
						'users.table.action.editSingle.button'
					)
				);
			}
			if (row.status === UserStatus.enabled && suspendAction) {
				res.push(
					getActionCell(
						() => suspendAction(row),
						'users.table.action.suspend.button'
					)
				);
			}
			if (
				activateAction &&
				row.status !== UserStatus.enabled &&
				row.status !== UserStatus.invited
			) {
				res.push(
					getActionCell(
						() => activateAction(row),
						'users.table.action.activate.button'
					)
				);
			}
			if (deleteAction) {
				res.push(
					getActionCell(
						() => deleteAction!(row),
						'users.table.action.delete.button'
					)
				);
			}
			return res;
		})
	];
};

export const getTenantsColumns = (
	deleteAction: TableCellAction
): GridColDef[] => {
	const prefix = 'fields.tenants.column';

	return [
		getColumn(prefix, TENANTS_FIELDS.name, ColWidth.m),
		getColumn(prefix, TENANTS_FIELDS.id, ColWidth.m),
		getColumn(prefix, TENANTS_FIELDS.selfProvisioningDomains, ColWidth.m),
		getColumn(prefix, TENANTS_FIELDS.version, ColWidth.m),
		getDateColumn(prefix, TENANTS_FIELDS.createdTime),
		getActionsColumn(({ row }) => [
			getActionCell(() => deleteAction(row), 'tenants.table.actions.delete')
		])
	];
};

export const getFlowsColumns = (
	disableAction?: TableCellAction,
	activateAction?: TableCellAction
): GridColDef[] => {
	const prefix = 'fields.flows.column';

	return [
		getColumn(prefix, FLOWS_FIELDS.id, ColWidth.m),
		getColumn(prefix, FLOWS_FIELDS.name, ColWidth.m),
		getColumn(prefix, FLOWS_FIELDS.description, ColWidth.xl),
		getColumn(prefix, FLOWS_FIELDS.disabled, ColWidth.s, FlowStatusCell),
		getActionsColumn(({ row }) => {
			const actions = [];
			if (row.disabled) {
				actions.push(
					getActionCell(
						() => activateAction?.(row),
						'flows.column.action.activate.button'
					)
				);
			} else {
				actions.push(
					getActionCell(
						() => disableAction?.(row),
						'flows.column.action.disable.button'
					)
				);
			}
			return actions;
		})
	];
};

export const getAccessKeysColumns = (): GridColDef[] => {
	const prefix = 'fields.accessKeys.column';

	return [
		...getCommonColumns(),
		getColumn(prefix, ACCESS_KEYS_FIELDS.name, ColWidth.l),
		getColumn(
			prefix,
			ACCESS_KEYS_FIELDS.status,
			ColWidth.m,
			AccessKeyStatusCell
		),
		getDateColumn(prefix, ACCESS_KEYS_FIELDS.expireTime, ExpirationCell),
		getColumn(prefix, ACCESS_KEYS_FIELDS.createdBy, ColWidth.m),
		getColumn(prefix, ACCESS_KEYS_FIELDS.project, ColWidth.m),
		...getAuthColumns(),
		getColumn(prefix, ACCESS_KEYS_FIELDS.hash, ColWidth.l),
		getColumn(
			prefix,
			ACCESS_KEYS_FIELDS.permittedProjects,
			ColWidth.l,
			ProjectsCell
		)
	];
};

export const getPermissionsColumns = (): GridColDef[] => {
	const prefix = 'fields.permissions.column';

	return [
		getColumn(prefix, PERMISSIONS_FIELDS.name, ColWidth.l),
		getColumn(prefix, PERMISSIONS_FIELDS.description, ColWidth.xl),
		getColumn(
			prefix,
			PERMISSIONS_FIELDS.systemDefault,
			ColWidth.s,
			undefined,
			(p: GridCellParams) => p?.row?.systemDefault || false
		),
		...getCommonColumns()
	];
};

export const getRolesColumns = (): GridColDef[] => {
	const prefix = 'fields.roles.column';

	return [
		getColumn(prefix, ROLES_FIELDS.name, ColWidth.l),
		getColumn(prefix, ROLES_FIELDS.description, ColWidth.xl),
		getColumn(
			prefix,
			ROLES_FIELDS.permissions,
			ColWidth.xl,
			undefined,
			(params: GridCellParams) =>
				compact((params?.value || []).map((per: IPermission) => per.name)).join(
					', '
				)
		),
		getColumn(
			prefix,
			ROLES_FIELDS.systemDefault,
			ColWidth.s,
			undefined,
			(p: GridCellParams) => p?.row?.systemDefault || false
		),
		...getCommonColumns()
	];
};

export const getAuditsColumns = (): GridColDef[] => {
	const prefix = 'fields.audits.column';

	return [
		getDateColumn(prefix, AUDITS_FIELDS.occurred, undefined, false),
		getColumn(
			prefix,
			AUDITS_FIELDS.tenants,
			ColWidth.m,
			AuditTenantsCell,
			undefined,
			false
		),
		getColumn(
			prefix,
			AUDITS_FIELDS.userId,
			ColWidth.m,
			undefined,
			undefined,
			false
		),
		getColumn(
			prefix,
			'actionType',
			ColWidth.m,
			AuditActionTypeCell,
			(params: GridCellParams) => params.row?.action,
			false
		),
		getColumn(
			prefix,
			AUDITS_FIELDS.action,
			ColWidth.l,
			undefined,
			undefined,
			false
		)
	];
};
